<!--
  PdfViewerDialog.vue

  @version 1.0.1 (FE v2.174.1: worker come .js, dettaglio errore a video)
  @description Visualizzatore PDF in overlay per la PWA standalone iOS: X per
               chiudere, tutte le pagine scorrevoli, zoom e Condividi (menu di
               condivisione di iOS con il file: Mail, WhatsApp, Salva su File,
               Stampa). Montato una sola volta in App.vue, stato in
               `usePdfViewer`.

  Rendering con pdf.js (build LEGACY: quella standard da v4 richiede iOS
  17.4+), caricato a richiesta al primo PDF: né la libreria né il worker
  entrano nel bundle iniziale.

  Memoria (iPhone): si disegnano solo le pagine vicine alla vista
  (IntersectionObserver), quelle lontane vengono liberate; ogni canvas è
  limitato a MAX_CANVAS_PIXELS. Il nuovo canvas sostituisce il vecchio solo a
  disegno finito, così allo zoom la pagina non lampeggia.

  Regole iOS già adottate nei dialog fullscreen (FE v2.67.12):
  scroll-strategy "none", 100dvh, safe-area, overscroll-behavior: contain.
-->
<template>
  <v-dialog
    :model-value="state.open"
    fullscreen
    scroll-strategy="none"
    transition="dialog-bottom-transition"
    @update:model-value="onModelUpdate"
  >
    <v-card class="pdfv">
      <header class="pdfv__bar">
        <gg-btn-icon icon="ms:close" aria-label="Chiudi" @click="close" />
        <span class="pdfv__title">{{ state.title }}</span>
        <gg-btn-icon
          :icon="canShareFile ? 'ms:ios-share' : 'ms:download'"
          :aria-label="canShareFile ? 'Condividi' : 'Scarica'"
          :disabled="!state.blob"
          @click="onShare"
        />
      </header>

      <div ref="scroller" class="pdfv__scroller">
        <div v-if="errorMessage" class="pdfv__state">
          <v-icon size="40" class="mb-2">ms:picture-as-pdf-outline</v-icon>
          <p>{{ errorMessage }}</p>
          <p v-if="errorDetail" class="pdfv__error-detail">{{ errorDetail }}</p>
        </div>
        <div v-else-if="busy" class="pdfv__state">
          <v-progress-circular indeterminate />
          <p class="mt-3">Caricamento PDF…</p>
        </div>

        <div v-if="pages.length" class="pdfv__inner">
          <div
            v-for="p in pages"
            :key="p.num"
            :ref="(el) => bindWrapper(p.num, el)"
            class="pdfv__page"
            :data-page="p.num"
            :style="pageStyle(p)"
          />
        </div>
      </div>

      <div v-if="pages.length" class="pdfv__zoom">
        <gg-btn-icon
          icon="ms:zoom-out"
          aria-label="Riduci"
          :disabled="zoomIndex === 0"
          @click="stepZoom(-1)"
        />
        <span class="pdfv__zoom-label">{{ Math.round(zoom * 100) }}%</span>
        <gg-btn-icon
          icon="ms:zoom-in"
          aria-label="Ingrandisci"
          :disabled="zoomIndex === ZOOM_STEPS.length - 1"
          @click="stepZoom(1)"
        />
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, shallowRef, watch } from 'vue'
import type { ComponentPublicInstance } from 'vue'
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist/legacy/build/pdf.mjs'
import { usePdfViewer } from '@/composables/usePdfViewer'
import { useSnackbar } from '@/composables/useSnackbar'

defineOptions({ name: 'PdfViewerDialog' })

// -----------------------------------------------------------------------------
// pdf.js a richiesta (una sola volta per sessione)
// -----------------------------------------------------------------------------

type PdfJs = typeof import('pdfjs-dist/legacy/build/pdf.mjs')
let pdfjsPromise: Promise<PdfJs> | null = null

function loadPdfJs(): Promise<PdfJs> {
  pdfjsPromise ??= Promise.all([
    import('pdfjs-dist/legacy/build/pdf.mjs'),
    // `?worker` e non `?url` (FE v2.174.1): Vite emette il worker come .js
    // classico. Con `?url` restava .mjs, che nginx serve come
    // application/octet-stream (`mjs` non è in mime.types): Safari rifiuta il
    // modulo e pdf.js fallisce con "Setting up fake worker failed".
    import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?worker'),
  ])
    .then(([pdfjs, { default: PdfWorker }]) => {
      // Un solo worker per sessione, riusato da tutti i documenti
      pdfjs.GlobalWorkerOptions.workerPort ??= new PdfWorker()
      return pdfjs
    })
    .catch((e: unknown) => {
      pdfjsPromise = null // ritenta al prossimo PDF (es. rete assente)
      throw e
    })
  return pdfjsPromise
}

// -----------------------------------------------------------------------------
// Stato
// -----------------------------------------------------------------------------

interface PageInfo {
  num: number
  /** Dimensioni a scala 1 (punti PDF) */
  width: number
  height: number
}

/** Limite per canvas: tiene la memoria sotto controllo sugli iPhone */
const MAX_CANVAS_PIXELS = 6_000_000
/** Larghezza massima della pagina a zoom 100% (iPad) */
const MAX_PAGE_WIDTH = 900
const PAGE_GUTTER = 8
const ZOOM_STEPS = [1, 1.5, 2, 3] as const

const { state, close } = usePdfViewer()
const { showError } = useSnackbar()

const scroller = ref<HTMLElement | null>(null)
const doc = shallowRef<PDFDocumentProxy | null>(null)
const pages = ref<PageInfo[]>([])
const renderError = ref<string | null>(null)
/** Messaggio tecnico sotto l'errore: sull'iPhone non c'è console da leggere */
const errorDetail = ref<string | null>(null)
const zoomIndex = ref(0)
const zoom = computed(() => ZOOM_STEPS[zoomIndex.value])
const containerWidth = ref(0)

const wrappers = new Map<number, HTMLElement>()
const visible = new Set<number>()
/** pagina → larghezza CSS a cui è disegnata */
const rendered = new Map<number, number>()
const tasks = new Map<number, RenderTask>()
/** pagina → generazione del disegno: scarta i disegni superati da uno più recente */
const pageGen = new Map<number, number>()
let observer: IntersectionObserver | null = null
let resizeObserver: ResizeObserver | null = null
let loadSeq = 0

const errorMessage = computed(() => state.error ?? renderError.value)
const busy = computed(() => state.loading || (!!state.blob && !pages.value.length))

const pageCssWidth = computed(() => {
  const base = Math.min(Math.max(containerWidth.value - PAGE_GUTTER * 2, 0), MAX_PAGE_WIDTH)
  return Math.round(base * zoom.value)
})

function pageStyle(p: PageInfo): Record<string, string> {
  const w = pageCssWidth.value
  return { width: `${w}px`, height: `${Math.round((w * p.height) / p.width)}px` }
}

// -----------------------------------------------------------------------------
// Caricamento documento
// -----------------------------------------------------------------------------

watch(
  () => state.blob,
  async (blob) => {
    await teardown()
    if (!blob) return
    const seq = ++loadSeq

    try {
      const pdfjs = await loadPdfJs()
      const data = new Uint8Array(await blob.arrayBuffer())
      const loaded = await pdfjs.getDocument({ data }).promise
      if (seq !== loadSeq) {
        void loaded.destroy()
        return
      }
      doc.value = loaded

      const infos: PageInfo[] = []
      for (let i = 1; i <= loaded.numPages; i++) {
        const page = await loaded.getPage(i)
        const vp = page.getViewport({ scale: 1 })
        infos.push({ num: i, width: vp.width, height: vp.height })
      }
      if (seq !== loadSeq) return

      measure()
      pages.value = infos
      await nextTick()
      setupObservers()
    } catch (e) {
      if (seq !== loadSeq) return
      console.error('[pdf-viewer] caricamento non riuscito', e)
      renderError.value = 'Impossibile visualizzare il PDF'
      errorDetail.value = e instanceof Error ? e.message : String(e)
    }
  },
)

async function teardown(): Promise<void> {
  loadSeq++
  observer?.disconnect()
  observer = null
  resizeObserver?.disconnect()
  resizeObserver = null
  tasks.forEach((t) => t.cancel())
  tasks.clear()
  wrappers.forEach((_el, num) => releasePage(num))
  visible.clear()
  rendered.clear()
  pageGen.clear()
  pages.value = []
  renderError.value = null
  errorDetail.value = null
  zoomIndex.value = 0

  const current = doc.value
  doc.value = null
  if (current) await current.destroy()
}

onBeforeUnmount(() => {
  void teardown()
})

// -----------------------------------------------------------------------------
// Pagine visibili e disegno
// -----------------------------------------------------------------------------

function measure(): void {
  containerWidth.value = scroller.value?.clientWidth ?? window.innerWidth
}

function setupObservers(): void {
  const root = scroller.value
  if (!root) return

  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const num = Number((entry.target as HTMLElement).dataset.page)
        if (entry.isIntersecting) {
          visible.add(num)
          void renderPage(num)
        } else {
          visible.delete(num)
          releasePage(num)
        }
      }
    },
    // Una schermata e mezza sopra e sotto: le pagine sono pronte prima di arrivarci
    { root, rootMargin: '150% 0px' },
  )
  wrappers.forEach((el) => observer?.observe(el))

  resizeObserver = new ResizeObserver(() => measure())
  resizeObserver.observe(root)
}

function bindWrapper(num: number, el: Element | ComponentPublicInstance | null): void {
  if (el instanceof HTMLElement) {
    if (wrappers.get(num) !== el) {
      wrappers.set(num, el)
      observer?.observe(el)
    }
  } else {
    const old = wrappers.get(num)
    if (old) observer?.unobserve(old)
    wrappers.delete(num)
  }
}

async function renderPage(num: number): Promise<void> {
  const current = doc.value
  const wrapper = wrappers.get(num)
  const info = pages.value[num - 1]
  const cssWidth = pageCssWidth.value
  if (!current || !wrapper || !info || cssWidth <= 0) return
  if (rendered.get(num) === cssWidth) return

  tasks.get(num)?.cancel()
  const gen = (pageGen.get(num) ?? 0) + 1
  pageGen.set(num, gen)
  const isStale = () => doc.value !== current || pageGen.get(num) !== gen

  const page = await current.getPage(num)
  if (isStale()) return

  const dpr = Math.min(window.devicePixelRatio || 1, 3)
  const wanted = (cssWidth / info.width) * dpr
  const capped = Math.sqrt(MAX_CANVAS_PIXELS / (info.width * info.height))
  const viewport = page.getViewport({ scale: Math.min(wanted, capped) })

  const canvas = document.createElement('canvas')
  canvas.width = Math.floor(viewport.width)
  canvas.height = Math.floor(viewport.height)
  const ctx = canvas.getContext('2d')
  if (!ctx) return

  const task = page.render({ canvasContext: ctx, viewport })
  tasks.set(num, task)
  try {
    await task.promise
    if (isStale() || !visible.has(num)) {
      freeCanvas(canvas)
      return
    }
    // Sostituzione a disegno finito: niente pagina bianca durante lo zoom
    wrapper.querySelectorAll('canvas').forEach(freeCanvas)
    wrapper.replaceChildren(canvas)
    rendered.set(num, cssWidth)
  } catch (e) {
    freeCanvas(canvas)
    if ((e as { name?: string })?.name !== 'RenderingCancelledException') {
      console.error(`[pdf-viewer] pagina ${num} non disegnata`, e)
    }
  } finally {
    if (tasks.get(num) === task) tasks.delete(num)
  }
}

function releasePage(num: number): void {
  pageGen.set(num, (pageGen.get(num) ?? 0) + 1)
  tasks.get(num)?.cancel()
  tasks.delete(num)
  const wrapper = wrappers.get(num)
  if (wrapper) {
    wrapper.querySelectorAll('canvas').forEach(freeCanvas)
    wrapper.replaceChildren()
  }
  rendered.delete(num)
}

/** Su iOS la memoria del canvas si libera davvero solo azzerandone le dimensioni */
function freeCanvas(canvas: HTMLCanvasElement): void {
  canvas.width = 0
  canvas.height = 0
}

function rerenderVisible(): void {
  visible.forEach((num) => void renderPage(num))
}

watch(pageCssWidth, () => {
  void nextTick(rerenderVisible)
})

// -----------------------------------------------------------------------------
// Zoom (pagine ridisegnate alla nuova risoluzione, centro della vista mantenuto)
// -----------------------------------------------------------------------------

function stepZoom(delta: 1 | -1): void {
  const next = Math.min(Math.max(zoomIndex.value + delta, 0), ZOOM_STEPS.length - 1)
  if (next === zoomIndex.value) return

  const el = scroller.value
  const ratio = ZOOM_STEPS[next] / zoom.value
  const cx = el ? el.scrollLeft + el.clientWidth / 2 : 0
  const cy = el ? el.scrollTop + el.clientHeight / 2 : 0

  zoomIndex.value = next

  void nextTick(() => {
    if (!el) return
    el.scrollLeft = cx * ratio - el.clientWidth / 2
    el.scrollTop = cy * ratio - el.clientHeight / 2
  })
}

// -----------------------------------------------------------------------------
// Chiusura e condivisione
// -----------------------------------------------------------------------------

function onModelUpdate(value: boolean): void {
  if (!value) close()
}

const shareFile = computed<File | null>(() =>
  state.blob ? new File([state.blob], state.fileName, { type: 'application/pdf' }) : null,
)

const canShareFile = computed(() => {
  const file = shareFile.value
  return !!file && typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })
})

async function onShare(): Promise<void> {
  const file = shareFile.value
  if (!file) return

  if (canShareFile.value) {
    try {
      // Solo il file: su iOS aggiungere title/text fa arrivare a
      // alcune app (es. WhatsApp) il testo invece del PDF
      await navigator.share({ files: [file] })
    } catch (e) {
      if ((e as { name?: string })?.name !== 'AbortError') {
        showError('Condivisione non riuscita')
      }
    }
    return
  }

  // Browser senza condivisione di file: download
  const url = URL.createObjectURL(file)
  const a = document.createElement('a')
  a.href = url
  a.download = file.name
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}
</script>

<style lang="scss" scoped>
.pdfv {
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  border-radius: 0;
}

.pdfv__bar {
  flex: 0 0 auto;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 4px;
  padding: calc(env(safe-area-inset-top) + 4px) 8px 4px;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgb(var(--v-theme-surface));
}

.pdfv__title {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  font-weight: 600;
}

.pdfv__scroller {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  background: rgba(var(--v-theme-on-surface), 0.08);
}

.pdfv__inner {
  width: max-content;
  min-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 8px 8px calc(env(safe-area-inset-bottom) + 72px);
}

.pdfv__page {
  flex: 0 0 auto;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);

  :deep(canvas) {
    display: block;
    width: 100%;
    height: 100%;
  }
}

.pdfv__state {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
  color: rgba(var(--v-theme-on-surface), 0.7);
}

.pdfv__error-detail {
  margin-top: 8px;
  max-width: 320px;
  font-size: 0.75rem;
  word-break: break-word;
  opacity: 0.7;
}

.pdfv__zoom {
  position: absolute;
  left: 50%;
  bottom: calc(env(safe-area-inset-bottom) + 12px);
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  border-radius: 999px;
  background: rgb(var(--v-theme-surface));
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}

.pdfv__zoom-label {
  min-width: 48px;
  text-align: center;
  font-variant-numeric: tabular-nums;
  font-size: 0.875rem;
}
</style>
